import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Edit3, Loader2, MapPin, ReceiptText, UserRound, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { BillingReadiness } from '@/hooks/useBillingReadiness';
import { useTranslation } from 'react-i18next';
import {
  missingCandidateBillingFields,
  type CandidateBillingRequiredField,
} from '@/lib/billing/candidateBillingConfirmation';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

type BillingOwner = 'organization' | 'candidate';

interface BillingProfile {
  id?: string | null;
  full_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  billing_country_code?: string | null;
  billing_city?: string | null;
  billing_district?: string | null;
  billing_street?: string | null;
  billing_building_number?: string | null;
  billing_postal_code?: string | null;
  billing_additional_id_scheme?: string | null;
  billing_additional_id_value?: string | null;
  building_number?: string | null;
  street_name?: string | null;
  district?: string | null;
  city_name?: string | null;
  country_subentity?: string | null;
  postal_zone?: string | null;
  country_code?: string | null;
  legal_name?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_number?: string | null;
  vat_number?: string | null;
  additional_id_scheme?: string | null;
  additional_id_value?: string | null;
  is_b2b?: boolean | null;
}

interface OrganizationBillingRpcClient {
  rpc(
    name: 'get_forkhire_billing_confirmation_profile',
    args: { _org_id: string },
  ): PromiseLike<{ data: unknown; error: unknown }>;
}

interface BillingClient extends OrganizationBillingRpcClient {
  from?: SupabaseClient<Database>['from'];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function optionalText(value: unknown): string | null | undefined {
  return value === null ? null : typeof value === 'string' ? value : undefined;
}

function normalizeBillingProfile(value: unknown): BillingProfile {
  const profile = asRecord(value);
  return {
    id: optionalText(profile.id),
    full_name: optionalText(profile.full_name),
    email: optionalText(profile.email),
    phone_number: optionalText(profile.phone_number),
    billing_country_code: optionalText(profile.billing_country_code),
    billing_city: optionalText(profile.billing_city),
    billing_district: optionalText(profile.billing_district),
    billing_street: optionalText(profile.billing_street),
    billing_building_number: optionalText(profile.billing_building_number),
    billing_postal_code: optionalText(profile.billing_postal_code),
    billing_additional_id_scheme: optionalText(profile.billing_additional_id_scheme),
    billing_additional_id_value: optionalText(profile.billing_additional_id_value),
    building_number: optionalText(profile.building_number),
    street_name: optionalText(profile.street_name),
    district: optionalText(profile.district),
    city_name: optionalText(profile.city_name),
    country_subentity: optionalText(profile.country_subentity),
    postal_zone: optionalText(profile.postal_zone),
    country_code: optionalText(profile.country_code),
    legal_name: optionalText(profile.legal_name),
    contact_name: optionalText(profile.contact_name),
    contact_email: optionalText(profile.contact_email),
    contact_number: optionalText(profile.contact_number),
    vat_number: optionalText(profile.vat_number),
    additional_id_scheme: optionalText(profile.additional_id_scheme),
    additional_id_value: optionalText(profile.additional_id_value),
    is_b2b: typeof profile.is_b2b === 'boolean' ? profile.is_b2b : undefined,
  };
}

interface BillingConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerType: BillingOwner;
  ownerId?: string;
  client: BillingClient;
  title?: string;
  description?: string;
  editPath: string;
  candidateIdentityEditPath?: string;
  readiness?: BillingReadiness;
  isReadinessLoading?: boolean;
  isProcessing?: boolean;
  paymentState?: 'idle' | 'processing' | 'success' | 'failed';
  brand?: 'forkhire' | 'forkseek';
  pricingSummary?: ReactNode;
  pricingReady?: boolean;
  pricingLoading?: boolean;
  onConfirm: () => void;
}

export function BillingConfirmationDialog({
  open,
  onOpenChange,
  ownerType,
  ownerId,
  client,
  title,
  description,
  editPath,
  candidateIdentityEditPath,
  readiness,
  isReadinessLoading = false,
  isProcessing = false,
  paymentState = 'idle',
  brand = ownerType === 'candidate' ? 'forkseek' : 'forkhire',
  pricingSummary,
  pricingReady = true,
  pricingLoading = false,
  onConfirm,
}: BillingConfirmationDialogProps) {
  const { t } = useTranslation();

  const profileQuery = useQuery({
    queryKey: ['billing-confirmation-profile', ownerType, ownerId],
    enabled: open && !!ownerId,
    queryFn: async () => {
      if (!ownerId) throw new Error('Billing owner is required.');
      if (ownerType === 'organization') {
        const { data, error } = await client.rpc('get_forkhire_billing_confirmation_profile', {
          _org_id: ownerId,
        });
        if (error) throw error;
        return normalizeBillingProfile(data);
      }

      if (!client.from) {
        throw new Error('Candidate billing profile client is unavailable.');
      }

      const [{ data: seekProfile, error: seekError }, { data: candidateProfile, error: candidateError }] = await Promise.all([
        client
          .from('seek_profiles')
          .select('full_name, email, phone_number, billing_country_code, billing_city, billing_district, billing_street, billing_building_number, billing_postal_code, billing_additional_id_scheme, billing_additional_id_value')
          .eq('user_id', ownerId)
          .maybeSingle(),
        client
          .from('candidate_profiles')
          .select('phone, country, city')
          .eq('candidate_id', ownerId)
          .maybeSingle(),
      ]);
      if (seekError) throw seekError;
      if (candidateError) throw candidateError;
      return normalizeBillingProfile({
        ...(seekProfile ?? {}),
        phone_number: candidateProfile?.phone || seekProfile?.phone_number,
        billing_country_code: seekProfile?.billing_country_code || candidateProfile?.country || 'SA',
        billing_city: seekProfile?.billing_city || candidateProfile?.city,
      });
    },
  });

  const billingLines = useMemo(() => {
    const p = profileQuery.data ?? {};
    const notProvided = t('hire.billingConfirmation.notProvided');
    if (ownerType === 'candidate') {
      return [
        { label: t('hire.billingConfirmation.labels.name'), value: p.full_name },
        { label: t('hire.billingConfirmation.labels.email'), value: p.email },
        { label: t('hire.billingConfirmation.labels.phone'), value: p.phone_number || notProvided },
        { label: t('hire.billingConfirmation.labels.billingAddress'), value: [p.billing_building_number, p.billing_street, p.billing_district, p.billing_city, p.billing_postal_code, p.billing_country_code].filter(Boolean).join(', ') },
        { label: t('hire.billingConfirmation.labels.additionalId'), value: p.billing_additional_id_scheme && p.billing_additional_id_value ? `${p.billing_additional_id_scheme} ${p.billing_additional_id_value}` : notProvided },
      ];
    }
    const address = [p.building_number, p.street_name, p.district, p.city_name, p.country_subentity, p.postal_zone, p.country_code]
      .filter(Boolean)
      .join(', ');
    const lines = [
      {
        label: p.is_b2b === true
          ? t('hire.billingConfirmation.labels.legalName')
          : t('hire.billingConfirmation.labels.billingName'),
        value: p.legal_name,
      },
      { label: t('hire.billingConfirmation.labels.billingContact'), value: [p.contact_name, p.contact_email, p.contact_number].filter(Boolean).join(' · ') || notProvided },
      {
        label: t('hire.billingConfirmation.labels.invoiceType'),
        value: p.is_b2b === true
          ? t('hire.billingConfirmation.invoiceTypes.standardTax')
          : p.is_b2b === false
            ? t('hire.billingConfirmation.invoiceTypes.simplified')
            : notProvided,
      },
      { label: t('hire.billingConfirmation.labels.billingAddress'), value: address },
    ];
    if (p.is_b2b === true) {
      lines.splice(2, 0,
        { label: t('hire.billingConfirmation.labels.vatNumber'), value: p.vat_number || notProvided },
        { label: t('hire.billingConfirmation.labels.additionalId'), value: p.additional_id_scheme && p.additional_id_value ? `${p.additional_id_scheme} ${p.additional_id_value}` : notProvided },
      );
    }
    return lines;
  }, [ownerType, profileQuery.data, t]);

  const organizationProfile = profileQuery.data ?? {};
  const hasBillingProfile = ownerType !== 'organization' || Boolean(organizationProfile.id);
  const organizationInvoiceReady = ownerType !== 'organization' || Boolean(
    hasBillingProfile &&
    typeof organizationProfile.is_b2b === 'boolean' &&
    organizationProfile.legal_name &&
    organizationProfile.street_name &&
    organizationProfile.city_name &&
    organizationProfile.country_code,
  );
  const candidateProfile = profileQuery.data ?? {};
  const candidateMissingFields = ownerType === 'candidate'
    ? missingCandidateBillingFields(candidateProfile)
    : [];
  const candidateReady = ownerType !== 'candidate' || candidateMissingFields.length === 0;
  const organizationReady = ownerType !== 'organization' || Boolean(readiness?.ready && organizationInvoiceReady);
  const profileStatusLoading = isReadinessLoading || profileQuery.isLoading;
  const profileReady = ownerType === 'candidate' ? candidateReady : organizationReady;
  const canContinue = (ownerType === 'candidate' ? candidateReady : organizationReady) && pricingReady;
  const consentLocked = isProcessing || paymentState !== 'idle';
  const accent = brand === 'forkseek' ? {
    text: 'text-seek',
    bg: 'bg-seek/10',
    border: 'border-seek/40',
    button: 'bg-seek text-seek-foreground hover:bg-seek/90',
  } : {
    text: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/40',
    button: '',
  };

  const candidateIdentityFields = new Set<CandidateBillingRequiredField>([
    'legal_customer_name',
    'billing_email',
    'phone_number',
  ]);
  const candidateNeedsIdentityEdit = candidateMissingFields.some((field) => candidateIdentityFields.has(field));
  const governedEditPath = ownerType === 'candidate' && candidateNeedsIdentityEdit
    ? candidateIdentityEditPath ?? editPath
    : editPath;
  const openGovernedEditPath = () => {
    onOpenChange(false);
    window.location.assign(governedEditPath);
  };
  const dialogTitle = title ?? t('hire.billingConfirmation.title');
  const dialogDescription = description ?? t('hire.billingConfirmation.description');

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!consentLocked) onOpenChange(next); }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className={`h-5 w-5 ${accent.text}`} />
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {paymentState !== 'idle' ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border p-8 text-center">
            {paymentState === 'processing' ? (
              <Loader2 className={`mb-4 h-12 w-12 animate-spin ${accent.text}`} />
            ) : paymentState === 'success' ? (
              <CheckCircle2 className={`mb-4 h-12 w-12 ${accent.text}`} />
            ) : (
              <XCircle className="mb-4 h-12 w-12 text-destructive" />
            )}
            <h3 className="text-lg font-semibold text-foreground">
              {paymentState === 'processing' ? t('hire.billingConfirmation.payment.processingTitle') : paymentState === 'success' ? t('hire.billingConfirmation.payment.successTitle') : t('hire.billingConfirmation.payment.failedTitle')}
            </h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              {paymentState === 'processing'
                ? t('hire.billingConfirmation.payment.processingDescription')
                : paymentState === 'success'
                  ? t('hire.billingConfirmation.payment.successDescription')
                  : t('hire.billingConfirmation.payment.failedDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pricingSummary}
            {!pricingReady && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {pricingLoading
                  ? t('pricingDisplay.loading', { defaultValue: 'Loading authoritative pricing…' })
                  : t('pricingDisplay.unavailable', { defaultValue: 'Authoritative pricing is unavailable. Checkout is disabled.' })}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent.bg}`}>
                  {ownerType === 'candidate' ? <UserRound className={`h-5 w-5 ${accent.text}`} /> : <MapPin className={`h-5 w-5 ${accent.text}`} />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('hire.billingConfirmation.profileTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('hire.billingConfirmation.profileDescription')}</p>
                </div>
              </div>
              {profileStatusLoading ? (
                <Badge variant="outline">{t('hire.billingConfirmation.checking')}</Badge>
              ) : !profileReady ? (
                <Badge variant="outline" className="border-destructive/40 text-destructive">{t('hire.billingConfirmation.incomplete')}</Badge>
              ) : (
                <Badge variant="outline" className={`${accent.border} ${accent.text}`}>{t('hire.billingConfirmation.ready')}</Badge>
              )}
            </div>

            <div className="rounded-lg border border-border">
              {profileQuery.isLoading ? (
                <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t('hire.billingConfirmation.loadingBillingInformation')}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {billingLines.map((line) => (
                    <div key={line.label} className="grid gap-1 p-4 sm:grid-cols-[150px_1fr] sm:gap-4">
                      <span className="text-xs font-medium uppercase text-muted-foreground">{line.label}</span>
                      <span className="text-sm text-foreground">{line.value || t('hire.billingConfirmation.notProvided')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!profileStatusLoading && !profileReady && (
              <div className="space-y-2 text-sm text-destructive">
                <p>{t('hire.billingConfirmation.completeMissingDetails')}</p>
                {ownerType === 'candidate' && candidateMissingFields.length > 0 && (
                  <ul className="list-disc space-y-1 ps-5" aria-label={t('hire.billingConfirmation.requiredFields')}>
                    {candidateMissingFields.map((field) => (
                      <li key={field}>{t(`hire.billingConfirmation.requiredFieldLabels.${field}`)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {ownerType === 'organization' ? (
                <Button type="button" variant="outline" onClick={openGovernedEditPath}>
                  <Edit3 className="me-2 h-4 w-4" />
                  {hasBillingProfile ? t('hire.billingConfirmation.editBillingInformation') : t('hire.billingConfirmation.addBillingInformation')}
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={openGovernedEditPath}>
                  <Edit3 className="me-2 h-4 w-4" />{t('hire.billingConfirmation.editInMyAccount')}
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {paymentState === 'success' ? (
            <Button type="button" className={accent.button} onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
          ) : paymentState === 'processing' ? null : paymentState === 'failed' ? (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={consentLocked} onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
              <Button type="button" className={accent.button} disabled={!canContinue || !candidateReady || consentLocked || profileQuery.isLoading} onClick={onConfirm}>
                {isProcessing ? <><Loader2 className="me-2 h-4 w-4 animate-spin" />{t('hire.billingConfirmation.processing')}</> : t('hire.billingConfirmation.confirmAndContinue')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
